"use client";

import {
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  type RefObject,
} from "react";

import { calculateNavamsa, type D9Planet, type NavamsaResponse } from "@/lib/api/navamsa";
import { trackCalculatorUsed } from "@/lib/analytics";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function VargottamaBadge() {
  return (
    <span
      className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
      style={{ background: "var(--ish-accent)", color: "#fff" }}
    >
      Vargottama
    </span>
  );
}

function D9Table({ planets }: { planets: D9Planet[] }) {
  return (
    <div className="overflow-x-auto mt-6">
      <table className="w-full text-[13px]" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr
            className="text-left text-[11px] font-semibold uppercase tracking-wide"
            style={{ color: "var(--ish-accent)", borderBottom: "1px solid var(--ish-border)" }}
          >
            <th className="py-2 pr-3">Planet</th>
            <th className="py-2 pr-3">D1 Rashi</th>
            <th className="py-2 pr-3">D9 Rashi</th>
            <th className="py-2 pr-3">D9 House</th>
            <th className="py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {planets.map((p) => (
            <tr
              key={p.planet_id}
              style={{ borderBottom: "1px solid var(--ish-border)" }}
              className={p.is_vargottama ? "font-semibold" : ""}
            >
              <td className="py-2 pr-3" style={{ color: "var(--ish-dark)" }}>
                {p.planet_display_en}
                <span className="ml-1 text-[11px]" style={{ color: "var(--ish-text-muted)" }}>
                  {p.planet_display_hi}
                </span>
              </td>
              <td className="py-2 pr-3" style={{ color: "var(--ish-text-muted)" }}>
                {p.d1_rashi_display_en}
              </td>
              <td className="py-2 pr-3" style={{ color: "var(--ish-dark)" }}>
                {p.d9_rashi_display_en}
              </td>
              <td className="py-2 pr-3" style={{ color: "var(--ish-text-muted)" }}>
                {p.d9_house}
              </td>
              <td className="py-2">
                {p.is_vargottama ? (
                  <VargottamaBadge />
                ) : (
                  <span style={{ color: "var(--ish-text-muted)" }}>—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PredictionsList({ predictions }: { predictions: string[] }) {
  return (
    <div className="mt-6 space-y-2">
      {predictions.map((p, i) => (
        <div key={i} className="card-sacred p-3 text-[13px]" style={{ color: "var(--ish-dark)" }}>
          {p}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main client component
// ---------------------------------------------------------------------------

interface FormState {
  name: string;
  birth_date: string;
  birth_time: string;
  birth_place: string;
  latitude: string;
  longitude: string;
  timezone: string;
}

const DEFAULT_FORM: FormState = {
  name: "",
  birth_date: "",
  birth_time: "12:00",
  birth_place: "",
  latitude: "28.6139",
  longitude: "77.2090",
  timezone: "5.5",
};

export default function NavamsaChartClient() {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<NavamsaResponse | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setResult(null);

    try {
      const data = await calculateNavamsa({
        birth_date: form.birth_date,
        birth_time: form.birth_time || "12:00",
        birth_place: form.birth_place,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        timezone: parseFloat(form.timezone),
      });
      setResult(data);
      trackCalculatorUsed({ calculator: "navamsa_chart" });
      setTimeout(
        () => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        100
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="mb-10">
      <NavamsaForm
        form={form}
        loading={loading}
        error={error}
        onChange={handleChange}
        onSubmit={handleSubmit}
      />
      {result && <NavamsaResults result={result} resultRef={resultRef} />}
    </section>
  );
}

function NavamsaForm({
  form,
  loading,
  error,
  onChange,
  onSubmit,
}: {
  form: FormState;
  loading: boolean;
  error: string | null;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <div
      className="card-sacred p-6 rounded-2xl"
      style={{ borderTop: "3px solid var(--ish-accent)" }}
    >
      <h2
        className="text-[20px] font-bold mb-1"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        Get Your Navamsa Chart (D9)
      </h2>
      <p className="text-[13px] mb-5 text-ish-text-muted">
        Why do your relationships follow the same pattern? Your D9 chart holds the answer.
      </p>

      <form onSubmit={onSubmit} className="space-y-4">
        <NavamsaFields form={form} onChange={onChange} />
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-xl font-bold text-[15px] transition-opacity"
          style={{
            background: loading ? "var(--ish-border)" : "var(--ish-accent)",
            color: "#fff",
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? "Calculating D9 Chart…" : "Generate Navamsa Chart →"}
        </button>
      </form>

      {error && (
        <div
          className="mt-4 p-3 rounded-lg text-[13px]"
          style={{ background: "var(--ish-error-bg, #fef2f2)", color: "var(--ish-error)" }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

function NavamsaFields({
  form,
  onChange,
}: {
  form: FormState;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <NavamsaInput
        id="nc-birth-date"
        name="birth_date"
        label="Date of Birth"
        type="date"
        value={form.birth_date}
        onChange={onChange}
        required
      />
      <NavamsaInput
        id="nc-birth-time"
        name="birth_time"
        label={
          <>
            Time of Birth{" "}
            <span className="font-normal text-ish-text-muted">(leave 12:00 if unknown)</span>
          </>
        }
        type="time"
        value={form.birth_time}
        onChange={onChange}
      />
      <NavamsaInput
        id="nc-birth-place"
        name="birth_place"
        label="Birth City"
        type="text"
        value={form.birth_place}
        onChange={onChange}
        placeholder="e.g. New Delhi"
        required
        className="sm:col-span-2"
      />
      <NavamsaInput
        id="nc-lat"
        name="latitude"
        label="Latitude"
        type="number"
        value={form.latitude}
        onChange={onChange}
        step="0.0001"
        required
      />
      <NavamsaInput
        id="nc-lon"
        name="longitude"
        label="Longitude"
        type="number"
        value={form.longitude}
        onChange={onChange}
        step="0.0001"
        required
      />
    </div>
  );
}

function NavamsaInput({
  id,
  name,
  label,
  type,
  value,
  onChange,
  placeholder,
  step,
  required,
  className = "",
}: {
  id: string;
  name: keyof FormState;
  label: ReactNode;
  type: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  step?: string;
  required?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="block text-[12px] font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {label}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        step={step}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="w-full border rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2"
        style={{
          borderColor: "var(--ish-border)",
          color: "var(--ish-dark)",
          background: "#fff",
        }}
      />
    </div>
  );
}

function NavamsaResults({
  result,
  resultRef,
}: {
  result: NavamsaResponse;
  resultRef: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div ref={resultRef} className="mt-8 card-sacred p-6 rounded-2xl">
      <NavamsaSummary result={result} />
      <D9Table planets={result.d9_chart} />
      <div className="mt-8">
        <h4
          className="text-[16px] font-bold mb-2"
          style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
        >
          Planet-wise Readings
        </h4>
        <PredictionsList predictions={result.predictions} />
      </div>

      <p className="text-[11px] mt-6 text-ish-text-muted">
        Generated by Ishvaram Jyotish engine · Sidereal zodiac (Lahiri ayanamsa) · Reference: Brihat
        Parashara Hora Shastra
      </p>
    </div>
  );
}

function NavamsaSummary({ result }: { result: NavamsaResponse }) {
  return (
    <div className="mb-4">
      <p
        className="text-[12px] font-semibold uppercase tracking-wide"
        style={{ color: "var(--ish-accent)" }}
      >
        Navamsa D9 · {result.birth_place} · {result.birth_date}
      </p>
      <h3
        className="text-[20px] font-bold mt-1"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        Navamsa Lagna:{" "}
        <span style={{ color: "var(--ish-accent)" }}>{result.navamsa_lagna_display_en}</span>
        <span className="text-[14px] ml-2 text-ish-text-muted">
          {result.navamsa_lagna_display_hi}
        </span>
      </h3>
      {result.vargottama_count > 0 && (
        <p className="text-[13px] mt-1 text-ish-text-muted">
          <strong style={{ color: "var(--ish-dark)" }}>{result.vargottama_count}</strong> Vargottama
          planet{result.vargottama_count === 1 ? "" : "s"} — same rashi in D1 and D9 (exceptionally
          strong).
        </p>
      )}
    </div>
  );
}
